import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api-client";
import { useActiveLocationTimezone } from "@/hooks/use-locations";
import { useAuthStore } from "@/store/auth-store";
import { useAppStore } from "@/store/store";
import { fromApiReservation, fromApiReservationEvent } from "@/lib/adapters";
import type { ApiReservation, ApiReservationEvent, PaginatedResponse } from "@/types/api";
import type { Reservation } from "@/store/types";
import { parseInTz, todayStr } from "@/utils/date";

const RESERVATION_FIELDS = [
  "id",
  "guest_id",
  "guest_name",
  "guest_phone",
  "guest_email",
  "guest_is_risk",
  "pax",
  "starts_at",
  "ends_at",
  "tables",
  "preferred_zone_id",
  "status",
  "origin",
  "notes",
  "internal_notes",
  "occasion",
  "tags",
  "features",
  "is_vip",
  "is_confirmed",
  "seated_at",
  "cancelled_at",
  "pin_level",
  "delay_minutes",
  "confirmation_token",
  "manage_url",
  "origin_waitlist_entry_id",
  "created_at",
] as const satisfies readonly (keyof ApiReservation)[];

const RESERVATION_EVENT_FIELDS = [
  "event_type",
  "detail",
  "created_at",
  "actor_name",
  "caused_by_label",
] as const satisfies readonly (keyof ApiReservationEvent)[];

// ── Queries ──────────────────────────────────────────────────────

/** Merge a day's worth of fetched reservations into the store, replacing
 * only the rows whose date matches `syncDay`. Two queries (live-today and
 * the sidebar's selected day) feed the same `reservations` array, so each
 * one must scope its replacement to its own day or the other day's rows
 * get clobbered (the floor map then loses today's state when the sidebar
 * navigates away). */
function syncReservationsForDay(data: Reservation[], syncDay: string) {
  useAppStore.setState((state) => ({
    reservations: [...state.reservations.filter((r) => r.date !== syncDay), ...data],
  }));
}

export function useReservations(
  filters?: {
    location?: string;
    status?: string;
    starts_after?: string;
    starts_before?: string;
  },
  syncDay?: string,
) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);
  const locationTz = useActiveLocationTimezone();

  const query = useQuery({
    queryKey: ["reservations", organizationId, locationId, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: RESERVATION_FIELDS.join(",") });
      if (filters?.location) params.set("location", filters.location);
      if (filters?.status) params.set("status", filters.status);
      if (filters?.starts_after) params.set("starts_after", filters.starts_after);
      if (filters?.starts_before) params.set("starts_before", filters.starts_before);
      const res = await api
        .get(`reservations/?${params}`)
        .json<PaginatedResponse<ApiReservation>>();
      return res.items.map((r) => fromApiReservation(r, locationTz));
    },
    enabled: isAuthenticated && !!organizationId && !!locationId && !!locationTz,
  });

  useEffect(() => {
    if (!query.data || !syncDay) return;
    syncReservationsForDay(query.data, syncDay);
  }, [query.data, syncDay]);

  return query;
}

// Shared hook for the "active day" query — keeps root and consumer components
// (Sidebar, ReservasPage) on the same queryKey so they hit the same cache.
// Consumers read `isPending` from the returned query to drive first-load UI.
export function useActiveReservations() {
  const selectedDate = useAppStore((s) => s.selectedDate);
  const locationTz = useActiveLocationTimezone();
  const filters = useMemo(() => {
    if (!locationTz) return undefined;
    const dayStart = parseInTz(selectedDate, "YYYY-MM-DD", locationTz);
    return {
      starts_after: dayStart.toISOString(),
      starts_before: dayStart.add(1, "day").toISOString(),
    };
  }, [selectedDate, locationTz]);
  return useReservations(filters, selectedDate);
}

const SEARCH_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_LEN = 2;

/**
 * Cross-date reservation search powering the staff sidebar's "buscador". When
 * the input has text, we drop the calendar window and ask the backend to match
 * against guest name / phone / email across every date.
 *
 * Critical: results are returned directly to the caller — they MUST NOT flow
 * into the global Zustand store. The store is sliced per-day (see
 * ``syncReservationsForDay``) and merging multi-date hits would clobber the
 * floor map's live view.
 */
export function useReservationSearch(search: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);
  const locationTz = useActiveLocationTimezone();

  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const enabled =
    isAuthenticated &&
    !!organizationId &&
    !!locationId &&
    !!locationTz &&
    debouncedSearch.length >= SEARCH_MIN_LEN;

  const query = useInfiniteQuery({
    queryKey: ["reservations-search", organizationId, locationId, debouncedSearch],
    enabled,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        fields: RESERVATION_FIELDS.join(","),
        search: debouncedSearch,
        limit: String(SEARCH_PAGE_SIZE),
        offset: String(pageParam),
      });
      const res = await api
        .get(`reservations/?${params}`)
        .json<PaginatedResponse<ApiReservation>>();
      return {
        items: res.items.map((r) => fromApiReservation(r, locationTz)),
        count: res.count,
        offset: res.offset,
        limit: res.limit,
      };
    },
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      return nextOffset < lastPage.count ? nextOffset : undefined;
    },
  });

  const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const count = query.data?.pages[0]?.count ?? 0;

  return {
    items,
    count,
    isPending: query.isPending && enabled,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    // ``true`` while the user is mid-typing (input has text but debounce
    // hasn't settled). Lets the consumer keep the search UI active without
    // showing a "no results" message during the typing window.
    isDebouncing: search.trim() !== debouncedSearch,
    isActive: debouncedSearch.length >= SEARCH_MIN_LEN,
  };
}

// Always-on query for the real calendar day. The floor map reads from this
// slice so its state stays anchored to "now" even when the sidebar navigates
// to other days. Without it, browsing tomorrow would overwrite today's
// reservations in the store and the map would lose its live view.
export function useLiveTodayReservations() {
  const today = todayStr();
  const locationTz = useActiveLocationTimezone();
  const filters = useMemo(() => {
    if (!locationTz) return undefined;
    const dayStart = parseInTz(today, "YYYY-MM-DD", locationTz);
    return {
      starts_after: dayStart.toISOString(),
      starts_before: dayStart.add(1, "day").toISOString(),
    };
  }, [today, locationTz]);
  return useReservations(filters, today);
}

export function useReservation(id: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const locationTz = useActiveLocationTimezone();

  return useQuery({
    queryKey: ["reservations", id, locationTz],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: RESERVATION_FIELDS.join(",") });
      const res = await api.get(`reservations/${id}/?${params}`).json<ApiReservation>();
      return fromApiReservation(res, locationTz);
    },
    enabled: isAuthenticated && !!id && !!locationTz,
  });
}

export function useReservationEvents(id: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: ["reservations", id, "events"],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: RESERVATION_EVENT_FIELDS.join(",") });
      const res = await api
        .get(`reservations/${id}/events/?${params}`)
        .json<PaginatedResponse<ApiReservationEvent>>();
      return res.items.map(fromApiReservationEvent);
    },
    enabled: isAuthenticated && !!id,
  });
}

// ── Table Suggestions ────────────────────────────────────────────

interface ApiTableMoveSuggestion {
  reservation_id: string;
  from_table_ids: string[];
  to_table_ids: string[];
  // Labels resolved server-side so the modal can render
  // "Mover Natalia (5p) de T8+T9 a 6+7" without needing the moved
  // reservation in the staff store. Default empty string / 0 when
  // older greedy callers don't populate them.
  guest_name?: string;
  pax?: number;
}

interface ApiTableGroupSuggestion {
  table_ids: string[];
  total_min: number;
  total_max: number;
  wasted_max: number;
  zone_id: string | null;
  // Cross-zone redistribute fallback: when no free option fits and no
  // single-swap redistribute works, the backend emits multi-relocation
  // plans here. The modal forwards these as ``moves`` on create so the
  // pin-override flow kicks in.
  requires_move?: ApiTableMoveSuggestion | null;
  requires_moves?: ApiTableMoveSuggestion[];
}

interface ApiWalkinPlan {
  anchor_table_id: string;
  table_ids: string[];
  total_min: number;
  total_max: number;
  max_available_pax: number;
}

export function useSuggestTables(params: {
  pax: number;
  startsAt: string;
  endsAt: string;
  preferredZone?: string;
  features?: string[];
  enabled?: boolean;
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["suggest-tables", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        pax: String(params.pax),
        starts_at: params.startsAt,
        ends_at: params.endsAt,
      });
      if (params.preferredZone) searchParams.set("preferred_zone", params.preferredZone);
      if (params.features?.length) searchParams.set("features", params.features.join(","));
      const raw = await api
        .get(`reservations/suggest-tables/?${searchParams}`)
        .json<ApiTableGroupSuggestion[] | PaginatedResponse<ApiTableGroupSuggestion>>();
      const items = Array.isArray(raw) ? raw : raw.items;
      return items.map((s) => {
        const moveChain =
          s.requires_moves && s.requires_moves.length > 0
            ? s.requires_moves
            : s.requires_move
              ? [s.requires_move]
              : [];
        return {
          tableIds: s.table_ids,
          totalCapacity: s.total_max,
          wastedCapacity: s.wasted_max,
          // Carry the relocation chain when present so the modal can
          // surface it ("Sentar requiere mover 2 reservas") and forward
          // the moves to ``apiCreateReservation`` for the pin-override
          // path. ``fromTableIds`` / ``guestName`` / ``pax`` ride along
          // so the create-modal banner can render the full sentence
          // ("Mover Natalia (5p) de T8+T9 a 6+7") without needing the
          // moved reservation in the local store.
          //
          // Field names stay snake_case to match ``pendingMoves`` state
          // and ``apiCreateReservation``'s ``moves`` contract — the
          // modal seeds ``pendingMoves`` directly from this array.
          requiresMoves: moveChain.map((m) => ({
            reservation_id: m.reservation_id,
            tables: m.to_table_ids,
            fromTableIds: m.from_table_ids,
            guestName: m.guest_name ?? "",
            pax: m.pax ?? 0,
          })),
        };
      });
    },
    enabled: isAuthenticated && !!locationId && params.pax > 0 && params.enabled !== false,
    // A mesa suggestion is only valid against the floor *right now*. Drop
    // it the moment the modal closes (no observers) so reopening always
    // refetches instead of flashing a stale suggestion that may point at a
    // table booked since — the "te sugiero la mesa pero al crear sale
    // ocupada" 409. Paired with the suggest-tables invalidation in
    // ``reservation-api.ts`` / ``use-realtime.ts`` for open modals.
    gcTime: 0,
  });
}

export function usePlanWalkin(
  tableId: string,
  pax: number,
  enabled = true,
  durationMinutes?: number,
) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["plan-walkin", tableId, pax, durationMinutes ?? null],
    queryFn: async () => {
      const params = new URLSearchParams({ table: tableId, pax: String(pax) });
      if (durationMinutes) params.set("duration_minutes", String(durationMinutes));
      return api.get(`reservations/plan-walkin/?${params}`).json<ApiWalkinPlan>();
    },
    enabled: isAuthenticated && !!locationId && !!tableId && pax > 0 && enabled,
    // Preserve the last successful response while a new pax value is in flight
    // so the walk-in panel doesn't flicker between data and pending on every
    // ± click. The new data will swap in once the request lands.
    placeholderData: (previousData) => previousData,
    // Don't retry the 409 ("anchor not free for this slot") — it's a
    // legitimate "no plan available" result and retrying just delays
    // the UI surfacing the fallback (single mesa, no combine).
    retry: false,
  });
}

// ── Helper to invalidate all reservation queries ─────────────────

function useInvalidateReservations() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  return () => queryClient.invalidateQueries({ queryKey: ["reservations", organizationId] });
}

// ── Mutations: CRUD ──────────────────────────────────────────────

export function useCreateReservationMut() {
  const invalidate = useInvalidateReservations();

  // Optional payload field ``acknowledge_kitchen_capacity_override``: when
  // the kitchen-capacity ceiling would be exceeded the backend returns a
  // 409 with body ``{detail, code: "kitchen_capacity_override_required",
  // context: {cap, current_pax, requested_pax, would_be, slot_local_time,
  // slot_local_date}}``. The caller should surface a confirmation modal
  // and, on confirm, retry the mutation with that flag set to ``true``.
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post("reservations/", { json: data }).json<ApiReservation>(),
    onSuccess: invalidate,
  });
}

export function useUpdateReservationMut() {
  const invalidate = useInvalidateReservations();

  // Same kitchen-cap override semantics as ``useCreateReservationMut`` —
  // the optional ``acknowledge_kitchen_capacity_override`` flag flows
  // straight through the spread payload.
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api.patch(`reservations/${id}/`, { json: data }).json<ApiReservation>(),
    onSuccess: invalidate,
  });
}

// ── Mutations: State Transitions ─────────────────────────────────

export function useSeatReservation() {
  const invalidate = useInvalidateReservations();

  return useMutation({
    mutationFn: (id: string) => api.post(`reservations/${id}/seat/`).json<ApiReservation>(),
    onSuccess: invalidate,
  });
}

export function useCompleteReservation() {
  const invalidate = useInvalidateReservations();

  return useMutation({
    mutationFn: (id: string) => api.post(`reservations/${id}/complete/`).json<ApiReservation>(),
    onSuccess: invalidate,
  });
}

export function useCancelReservationMut() {
  const invalidate = useInvalidateReservations();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`reservations/${id}/cancel/`, { json: { reason } }).json<ApiReservation>(),
    onSuccess: invalidate,
  });
}

export function useMarkNoShow() {
  const invalidate = useInvalidateReservations();

  return useMutation({
    mutationFn: (id: string) => api.post(`reservations/${id}/no-show/`).json<ApiReservation>(),
    onSuccess: invalidate,
  });
}

export function useRevertNoShowMut() {
  const invalidate = useInvalidateReservations();

  return useMutation({
    mutationFn: (id: string) =>
      api.post(`reservations/${id}/revert-no-show/`).json<ApiReservation>(),
    onSuccess: invalidate,
  });
}

export function useToggleConfirmed() {
  const invalidate = useInvalidateReservations();

  return useMutation({
    mutationFn: (id: string) =>
      api.post(`reservations/${id}/toggle-confirmed/`).json<ApiReservation>(),
    onSuccess: invalidate,
  });
}
